/**
 * v5.24 — CUSTOM CATERING PACKAGES: the browser's mirror of the server's
 * breakdown maths.
 *
 * THE SERVER IS THE ONLY THING THAT PRICES AN ORDER. Everything here is for
 * DISPLAY — the merchant's live balance check while they type, and the
 * customer's running total while they step categories up and down. The figure
 * a customer approves is re-derived by `price_catering_order` at checkout, so a
 * drift between these two spellings is a UX bug, never a money bug.
 *
 * It is still a mirror and it is still pinned: `services/catering_service.py`
 * owns the rule (`custom_slot_bounds` / `custom_breakdown` / `custom_price_range`
 * / `resolve_custom_price_per_person`) and this file must agree with it, the
 * same way the plan-id vocabulary and `dish_addon_price_key` are kept in two
 * spellings that a contract holds together.
 *
 * MONEY IS COMPARED IN WHOLE CENTS. Reconciliation is an equality test, and
 * float equality on money is how a breakdown that balances on screen fails to
 * balance on save.
 */

export const CUSTOM_MODE_OFF = 'off';
export const CUSTOM_MODE_SAME_TOTAL = 'same_total';

/** Mirrors `catering_service.is_custom_package`. */
export const isCustomPackage = (pkg) =>
  (pkg?.custom_mode || CUSTOM_MODE_OFF) === CUSTOM_MODE_SAME_TOTAL
  && (pkg?.pricing_model || 'per_person') === 'per_person';

/** Mirrors `catering_service.slot_included` — the count the package SELLS. */
export const slotIncluded = (slot) => {
  const n = parseInt(slot?.choose, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

/**
 * Mirrors `catering_service.custom_slot_bounds`. Defaults are what the setup
 * screen pre-fills: at least one of anything the package includes, at most one
 * more than it includes.
 */
export const customSlotBounds = (slot) => {
  const included = slotIncluded(slot);
  const rawMin = slot?.custom_min;
  const rawMax = slot?.custom_max;
  const parsedMin = parseInt(rawMin, 10);
  const parsedMax = parseInt(rawMax, 10);
  let min = (rawMin === null || rawMin === undefined || rawMin === '' || !Number.isFinite(parsedMin))
    ? (included >= 1 ? 1 : 0)
    : parsedMin;
  let max = (rawMax === null || rawMax === undefined || rawMax === '' || !Number.isFinite(parsedMax))
    ? included + 1
    : parsedMax;
  min = Math.max(0, min);
  max = Math.max(min, max);
  // v5.24 round 1 — A COUNT IS BOUNDED BY THE DISHES THAT EXIST. Mirrors the
  // server's clamp in `catering_service.custom_slot_bounds`: the pricer refuses
  // a repeated dish, so a category can never supply more items than it lists,
  // and a stepper offering a position that can never be filled is a dead end.
  // An EMPTY pool is an undeclared slot, not a zero — the server refuses that
  // by name at save time rather than clamping it here.
  const pool = Array.from(new Set((slot?.item_ids || []).filter(Boolean)));
  if (pool.length) {
    max = Math.min(max, pool.length);
    min = Math.min(min, max);
  }
  return { min, max };
};

/** Mirrors `catering_service._cents`. */
export const toCents = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/**
 * Mirrors `catering_service.custom_breakdown` — the merchant's reconciliation
 * report. `gap` is negative when the breakdown is UNDER the package price.
 */
export const customBreakdown = (pkg) => {
  const expectedC = toCents(pkg?.price_per_person);
  let totalC = 0;
  const rows = (pkg?.slots || []).map((slot) => {
    const included = slotIncluded(slot);
    const valueC = toCents(slot?.value_per_item);
    const lineC = included * valueC;
    totalC += lineC;
    const { min, max } = customSlotBounds(slot);
    return {
      slot_id: slot?.id,
      name: slot?.name,
      included,
      value_per_item: valueC / 100,
      line_total: lineC / 100,
      custom_min: min,
      custom_max: max,
      has_value: valueC > 0,
    };
  });
  return {
    expected: expectedC / 100,
    total: totalC / 100,
    gap: (totalC - expectedC) / 100,
    balanced: totalC === expectedC && expectedC > 0,
    rows,
  };
};

/**
 * Mirrors `catering_service.custom_price_range` — the cheapest and dearest
 * per-person price any LEGAL combination can reach.
 *
 * This is the line the merchant approves before saving. THE EXACT-TOTAL RULE
 * DOES NOT BOUND THE MONEY (five $1 desserts is $5 against a $19 package), so
 * showing the real floor is the honest way to present the bounds — they see
 * what they are agreeing to rather than discovering it on an invoice.
 */
export const customPriceRange = (pkg) => {
  const slots = pkg?.slots || [];
  if (!slots.length) return null;
  const target = slots.reduce((sum, s) => sum + slotIncluded(s), 0);
  let lowest = { 0: 0 };
  let highest = { 0: 0 };
  slots.forEach((slot) => {
    const { min, max } = customSlotBounds(slot);
    const valueC = toCents(slot?.value_per_item);
    const nextLow = {};
    const nextHigh = {};
    Object.entries(lowest).forEach(([usedKey, cost]) => {
      const used = parseInt(usedKey, 10);
      for (let n = min; n <= max; n += 1) {
        const k = used + n;
        if (k > target) break;
        const c = cost + n * valueC;
        if (nextLow[k] === undefined || c < nextLow[k]) nextLow[k] = c;
      }
    });
    Object.entries(highest).forEach(([usedKey, cost]) => {
      const used = parseInt(usedKey, 10);
      for (let n = min; n <= max; n += 1) {
        const k = used + n;
        if (k > target) break;
        const c = cost + n * valueC;
        if (nextHigh[k] === undefined || c > nextHigh[k]) nextHigh[k] = c;
      }
    });
    lowest = nextLow;
    highest = nextHigh;
  });
  if (lowest[target] === undefined) return null;
  return {
    total_items: target,
    min: lowest[target] / 100,
    max: highest[target] / 100,
  };
};

/** How many items the package sells in total — the number the picker gates on. */
export const customTotalItems = (pkg) =>
  (pkg?.slots || []).reduce((sum, s) => sum + slotIncluded(s), 0);

/**
 * Mirrors `catering_service.resolve_custom_price_per_person`.
 *
 * `counts` is {slot_id: how many picked}. Returns null when the mix is not a
 * legal order — the caller renders a prompt rather than a price, because a
 * number shown beside an unorderable basket is worse than no number.
 *
 * THE PACKAGE AS SOLD SHOWS THE ADVERTISED PRICE VERBATIM, exactly as the
 * server charges it, so an untouched package can never display a rounding cent
 * away from its own label.
 */
export const customPerPerson = (pkg, counts) => {
  const slots = pkg?.slots || [];
  if (!slots.length) return null;
  const required = customTotalItems(pkg);
  const picked = slots.reduce(
    (sum, s) => sum + (parseInt(counts?.[s?.id], 10) || 0), 0);
  if (picked !== required) return null;
  const untouched = slots.every(
    (s) => (parseInt(counts?.[s?.id], 10) || 0) === slotIncluded(s));
  if (untouched) return parseFloat(pkg?.price_per_person) || 0;
  let totalC = 0;
  for (let i = 0; i < slots.length; i += 1) {
    const valueC = toCents(slots[i]?.value_per_item);
    if (valueC <= 0) return null;
    totalC += (parseInt(counts?.[slots[i]?.id], 10) || 0) * valueC;
  }
  return totalC / 100;
};
